import {
  WORKSPACE_TIMEZONES,
  WORKSPACE_TIMEZONE_LABELS,
  type Workspace,
} from "@artisancode/api-types";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useWorkspaces } from "@/hooks/use-workspaces";

import { useWorkspaceForm } from "./use-workspace-form";

export function WorkspaceDialog({
  open,
  onOpenChange,
  editing,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  editing: Workspace | null;
}) {
  const { form, handleNameChange, isSubmitting, onSubmit } = useWorkspaceForm(
    open,
    editing,
    () => onOpenChange(false),
  );
  const { data: workspacesData } = useWorkspaces({ per_page: 100 });
  const workspaceOptions = workspacesData?.items ?? [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {editing ? "Edit Workspace" : "Tambah Workspace"}
          </DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form
            id="workspace-form"
            onSubmit={onSubmit}
            className="grid gap-3 py-2"
          >
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Nama Workspace</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Nama workspace"
                      autoFocus
                      {...field}
                      onChange={(e) => {
                        field.onChange(e);
                        handleNameChange(e.target.value);
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="slug"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Slug</FormLabel>
                  <FormControl>
                    <Input placeholder="slug-workspace" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="timezone"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Zona Waktu</FormLabel>
                  <Select value={field.value} onValueChange={field.onChange}>
                    <FormControl>
                      <SelectTrigger className="w-full">
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {WORKSPACE_TIMEZONES.map((tz) => (
                        <SelectItem key={tz} value={tz}>
                          {WORKSPACE_TIMEZONE_LABELS[tz]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormDescription className="text-[11px] text-muted-foreground">
                    Dipakai untuk pergantian tanggal/bulan nomor dokumen dan jam
                    kirim reminder.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            {!editing && (
              <FormField
                control={form.control}
                name="copyMasterFromWorkspaceId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Salin Master Data Dari (Opsional)</FormLabel>
                    <Select
                      value={field.value || "none"}
                      onValueChange={(val) =>
                        field.onChange(val === "none" ? "" : val)
                      }
                    >
                      <FormControl>
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder="Pilih workspace sumber..." />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="none">
                          ⚪ Tidak ada (Mulai dari awal)
                        </SelectItem>
                        {workspaceOptions.map((ws) => (
                          <SelectItem key={ws.id} value={ws.id}>
                            🏪 {ws.name} ({ws.slug})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormDescription className="text-[11px] text-muted-foreground">
                      Menyalin bahan baku, produk, resep, dan aturan bonus dari
                      workspace sumber. Stok dan seluruh transaksi tidak
                      disalin.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}
          </form>
        </Form>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
          >
            Batal
          </Button>
          <Button type="submit" form="workspace-form" disabled={isSubmitting}>
            {isSubmitting ? "Menyimpan..." : editing ? "Simpan" : "Tambah"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
